import React, { useRef, useEffect, useState } from 'react';
import * as atlas from 'azure-maps-control';
import 'azure-maps-control/dist/atlas.min.css';
import { polygon as turfPolygon } from '@turf/turf';
import bboxClip from '@turf/bbox-clip';
import area from '@turf/area';
 
const SanFranciscoParkingMap = () => {
  // Refs and state variables.
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const userMarkerRef = useRef(null);
 
  // State for backend data and UI.
  const [mergedIsochrones, setMergedIsochrones] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState({ hours: 12, minutes: 0 });
  const [coordinates, setCoordinates] = useState(null);
  const [address, setAddress] = useState('');
  const [parkingLots, setParkingLots] = useState([]);
  const [isochronePercentages, setIsochronePercentages] = useState({
    percentage5: 0,
    percentage10: 0,
    percentage15: 0,
    percentage20: 0,
  });
  const [infoMessage, setInfoMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const hostPath = process.env.REACT_APP_API_BASE_URL;
  const hostHeader = process.env.REACT_APP_CUSTOM_HEADER;
  const azureMapsKey = process.env.REACT_APP_AZURE_MAPS_KEY;

  //const parkingNearbyEndpoint = '${hostPath}/api/GetParkingIsochrones/api/GetParkingIsochrones';
  
 
  const getAddressFromCoordinates = async (latitude, longitude) => {
    const url = `https://atlas.microsoft.com/search/address/reverse/json?api-version=1.0&query=${latitude},${longitude}&subscription-key=${azureMapsKey}`.replace(/\s+/g, '');
    try {
      const response = await fetch(url);
      const data = await response.json();
      return data?.addresses?.[0]?.address?.freeformAddress || 'Unknown address';
    } catch (error) {
      console.error('Reverse Geocoding Error:', error);
      return 'Address lookup failed';
    }
  };
 
  const convertTo24HourFormat = (timeObj) => {
    const { hours, minutes } = timeObj;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };
 
  // Call the backend API using the provided address.
  const sendDataToBackend = async (addr) => {
    try {
      const formattedDate = selectedDate.toISOString().split('T')[0];
      const formattedTime = convertTo24HourFormat(selectedTime);
      const response = await fetch(   `${hostPath}/api/GetParkingIsochrones?address=${encodeURIComponent(addr)}&rangeKm=10&date=${formattedDate}&time=${formattedTime}`,{headers: { "x-functions-key": hostHeader }} );
      if (response.status === 404) {
        setInfoMessage('No parking lots found in the nearby area.');
        return;
      }
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const data = await response.json();
      let lots = data.parkingLots || [];
      if (!Array.isArray(lots)) {
        lots = [];
      }
      // Save merged isochrone polygons from the backend.
      setMergedIsochrones(data.mergedIsochrones);
      setParkingLots(lots);
      setInfoMessage('');
    } catch (error) {
      console.error('Fetch error:', error);
      setInfoMessage('Error fetching parking data. Please try again.');
    }
  };
 
  // Map click handler: update marker, reverse geocode and call backend API.
  const handleMapClick = async (event) => {
    if (!event.position) return;
    if (isLoading) return;
    setIsLoading(true);
    try {
      const [longitude, latitude] = event.position;
      setCoordinates([longitude, latitude]);
      // Get address from the clicked location.
      const locationAddress = await getAddressFromCoordinates(latitude, longitude);
      setAddress(locationAddress);
      // Add or update user marker.
      if (!userMarkerRef.current) {
        userMarkerRef.current = new atlas.HtmlMarker({
          position: [longitude, latitude],
          draggable: true,
          htmlContent: `
            <div style="
              color: white;
              width: 24px;
              height: 24px;
              border-radius: 50%;
              text-align: center;
              line-height: 24px;
              font-size: 14px;">
              📍
            </div>`
        });
        mapInstance.current.markers.add(userMarkerRef.current);
        mapInstance.current.events.add('dragend', userMarkerRef.current, async (e) => {
          const newPos = e.target.getOptions().position;
          setCoordinates(newPos);
          const newAddr = await getAddressFromCoordinates(newPos[1], newPos[0]);
          setAddress(newAddr);
          // Immediately call the API when the marker is dragged.
          await sendDataToBackend(newAddr);
        });
      } else {
        userMarkerRef.current.setOptions({ position: [longitude, latitude] });
      }
      // Re-center the map and call the API.
      mapInstance.current.setCamera({ center: [longitude, latitude], zoom: 13 });
      await sendDataToBackend(locationAddress);
    } catch (error) {
      console.error('Error in handleMapClick:', error);
    } finally {
      setIsLoading(false);
    }
  };
 
  // Initialize the Azure Map with San Francisco as the default center.
  useEffect(() => {
    if (!mapInstance.current) {
      mapInstance.current = new atlas.Map(mapRef.current, {
        center: [-122.406417, 37.785834], // San Francisco coordinates.
        zoom: 14,
        style: 'road',
        authOptions: {
          authType: atlas.AuthenticationType.subscriptionKey,
          subscriptionKey: azureMapsKey,
        },
      });
 
      mapInstance.current.events.add('ready', () => {
        mapInstance.current.controls.add(
          [
            new atlas.control.ZoomControl(),
            new atlas.control.CompassControl(),
            new atlas.control.StyleControl({
              mapStyles: [
                'road',
                'grayscale_dark',
                'grayscale_light',
                'night',
                'satellite',
                'satellite_road',
                'road_shaded_relief',
              ],
            }),
          ],
          { position: 'bottom-right' }
        );
        // Add click handler to trigger API call.
        mapInstance.current.events.add('click', handleMapClick);
      });
    }
  }, [azureMapsKey]);
 
  // --- Isochrone Rendering Logic ---
  useEffect(() => {
    if (!mapInstance.current || !mergedIsochrones) return;
 
    // Remove previous merged isochrone layers and sources.
    ['mergedIsochroneLayer20', 'mergedIsochroneLayer15', 'mergedIsochroneLayer10', 'mergedIsochroneLayer5'].forEach((id) => {
      const layer = mapInstance.current.layers.getLayerById(id);
      if (layer) mapInstance.current.layers.remove(layer);
    });
    ['mergedIsochroneSource20', 'mergedIsochroneSource15', 'mergedIsochroneSource10', 'mergedIsochroneSource5'].forEach((id) => {
      const source = mapInstance.current.sources.getById(id);
      if (source) mapInstance.current.sources.remove(source);
    });
 
    const addMergedIsochroneLayer = (duration, color, sourceId, layerId) => {
      const polygonsArray = mergedIsochrones[duration];
      if (!polygonsArray || polygonsArray.length === 0) return;
      const features = [];
      polygonsArray.forEach((poly) => {
        // Convert each point object to [lng, lat].
        const coords = poly.map((pt) => [pt.Lng || pt.lng, pt.Lat || pt.lat]);
        // Close the polygon ring if needed.
        if (
          coords.length > 0 &&
          (coords[0][0] !== coords[coords.length - 1][0] ||
           coords[0][1] !== coords[coords.length - 1][1])
        ) {
          coords.push(coords[0]);
        }
        try {
          const turfPoly = turfPolygon([coords]);
          features.push(turfPoly);
        } catch (err) {
          console.error(`Error creating turf polygon for duration ${duration}:`, err);
        }
      });
      if (features.length === 0) return;
      const source = new atlas.source.DataSource(sourceId);
      features.forEach((f) => {
        try {
          source.add(new atlas.data.Feature(f.geometry));
        } catch (error) {
          console.error(`Error adding feature for duration ${duration}:`, error);
        }
      });
      mapInstance.current.sources.add(source);
      const layer = new atlas.layer.PolygonLayer(source, layerId, {
        fillColor: color,
        fillOpacity: 0.5,
        strokeColor: color,
        strokeWidth: 2,
      });
      mapInstance.current.layers.add(layer);
    };
 
    // Add layers in order: 20 (bottom), then 15, 10, and 5.
    addMergedIsochroneLayer('20', 'rgba(219, 23, 19, 0.5)', 'mergedIsochroneSource20', 'mergedIsochroneLayer20');
    // addMergedIsochroneLayer('20', 'rgba(173, 216, 230, 1)', 'mergedIsochroneSource20', 'mergedIsochroneLayer20');
    addMergedIsochroneLayer('15', 'rgba(247, 139, 7, 0.8)', 'mergedIsochroneSource15', 'mergedIsochroneLayer15');
    addMergedIsochroneLayer('10', 'rgba(251, 198, 47, 0.8)', 'mergedIsochroneSource10', 'mergedIsochroneLayer10');
    addMergedIsochroneLayer('5', 'rgba(76, 175, 80, 0.9)', 'mergedIsochroneSource5', 'mergedIsochroneLayer5');
  }, [mergedIsochrones]);
 
  // --- Isochrone Percentage Calculation ---
  useEffect(() => {
    if (!mapInstance.current) return;
    const updateLegendForZoom = () => {
      const cameraBounds = mapInstance.current.getCamera().bounds;
      if (!mergedIsochrones) return;
      let visibleAreas = { "5": 0, "10": 0, "15": 0, "20": 0 };
 
      Object.keys(mergedIsochrones).forEach((duration) => {
        mergedIsochrones[duration].forEach((poly) => {
          try {
            const coords = poly.map(pt => [pt.Lng || pt.lng, pt.Lat || pt.lat]);
            if (
              coords.length > 0 &&
              (coords[0][0] !== coords[coords.length - 1][0] ||
               coords[0][1] !== coords[coords.length - 1][1])
            ) {
              coords.push(coords[0]);
            }
            const turfPoly = turfPolygon([coords]);
            const clipped = bboxClip(turfPoly, cameraBounds);
            visibleAreas[duration] += area(clipped);
          } catch (e) {
            console.error(`Error clipping ${duration}-min polygon:`, e);
          }
        });
      });
 
      const area5 = visibleAreas["5"];
      const area10Excl = Math.max(visibleAreas["10"] - visibleAreas["5"], 0);
      const area15Excl = Math.max(visibleAreas["15"] - visibleAreas["10"], 0);
      const area20Excl = Math.max(visibleAreas["20"] - visibleAreas["15"], 0);
      const totalVisibleArea = area5 + area10Excl + area15Excl + area20Excl;
      if (totalVisibleArea > 0) {
        setIsochronePercentages({
          percentage5: ((area5 / totalVisibleArea) * 100).toFixed(2),
          percentage10: ((area10Excl / totalVisibleArea) * 100).toFixed(2),
          percentage15: ((area15Excl / totalVisibleArea) * 100).toFixed(2),
          percentage20: ((area20Excl / totalVisibleArea) * 100).toFixed(2),
        });
      } else {
        setIsochronePercentages({
          percentage5: 0,
          percentage10: 0,
          percentage15: 0,
          percentage20: 0,
        });
      }
    };
 
    mapInstance.current.events.add('zoomend', updateLegendForZoom);
    mapInstance.current.events.add('moveend', updateLegendForZoom);
 
    return () => {
      mapInstance.current.events.remove('zoomend', updateLegendForZoom);
      mapInstance.current.events.remove('moveend', updateLegendForZoom);
    };
  }, [mergedIsochrones]);
 
  // --- Parking Lot Markers Rendering as DataSource and SymbolLayer ---
  // useEffect(() => {
  //   if (!mapInstance.current || parkingLots.length === 0) return;
 
  //   // If a parking data source already exists, remove its layer and source.
  //   const existingSource = mapInstance.current.sources.getById('parkingData');
  //   if (existingSource) {
  //     const existingLayer = mapInstance.current.layers.getLayerById('parkingLayer');
  //     if (existingLayer) {
  //       mapInstance.current.layers.remove(existingLayer);
  //     }
  //     mapInstance.current.sources.remove(existingSource);
  //   }
 
  //   // Create a new data source for parking.
  //   const parkingSource = new atlas.source.DataSource('parkingData');
  //   parkingLots.forEach((lot) => {
  //     const lng = Number(lot.longitude);
  //     const lat = Number(lot.latitude);
  //     if (isNaN(lng) || isNaN(lat)) {
  //       console.error('Invalid parking lot coordinate:', lot);
  //       return;
  //     }
  //     parkingSource.add(
  //       new atlas.data.Feature(
  //         new atlas.data.Point([lng, lat]),
  //         {
  //           title: lot.propertyAddress,
  //           distance: lot.distanceKm,
  //           time: lot.driveTimeMinutes,
  //         }
  //       )
  //     );
  //   });
  //   mapInstance.current.sources.add(parkingSource);
  //   const parkingLayer = new atlas.layer.SymbolLayer(parkingSource, 'parkingLayer', {
  //     iconOptions: { image: 'pin-round-darkblue', size: 1, allowOverlap: true },
  //   });
  //   mapInstance.current.layers.add(parkingLayer);
  // }, [parkingLots]);
 
  const handleRefreshClick = () => {
    window.location.reload();
  };
 
  return (
    <div style={{ width: '100%', height: '100vh', position: 'relative' }}>
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
 
      {/* Loading Overlay */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
        >
          <div
            style={{
              padding: 20,
              background: 'white',
              borderRadius: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}
          >
            Loading isochrones...
          </div>
        </div>
      )}
 
      {/* Control Panel (Date and Time selection) */}
      <div
        style={{
          position: 'absolute',
          top: 20,
          left: 20,
          background: 'white',
          padding: 15,
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          zIndex: 1000,
          width: 280,
        }}
      >
        <label style={{ display: 'block', marginBottom: 5 }}>
          Select Date:
          <input
            type="date"
            value={selectedDate.toISOString().split('T')[0]}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            style={{ width: '100%', padding: 8, marginTop: 5 }}
          />
        </label>
        <label style={{ display: 'block', margin: '15px 0 5px' }}>
          Select Time:
          <input
            type="time"
            value={`${selectedTime.hours.toString().padStart(2, '0')}:${selectedTime.minutes
              .toString()
              .padStart(2, '0')}`}
            onChange={(e) => {
              const [hh, mm] = e.target.value.split(':');
              setSelectedTime({ hours: Number(hh), minutes: Number(mm) });
            }}
            style={{ width: '100%', padding: 8, marginTop: 5 }}
          />
        </label>
      </div>
 
      {/* Info Message Box */}
      {infoMessage && (
        <div
          style={{
            position: 'absolute',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#ffcccc',
            padding: '10px 20px',
            borderRadius: '5px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 1100,
          }}
        >
          {infoMessage}
        </div>
      )}
 
      {/* Isochrone Legend */}
      <div
        style={{
          position: 'absolute',
          bottom: 140,
          left: 20,
          background: 'white',
          padding: '10px',
          borderRadius: '5px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          zIndex: 1000,
          width: 220,
        }}
      >
        <div style={{ marginBottom: '5px', fontWeight: 'bold' }}>Drive Time Isochrones</div>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
          {/* 5 minutes: matches isochrone: rgba(76, 175, 80, 0.7) */}
          <div style={{ width: '15px', height: '15px', background: 'rgba(76, 175, 80, 1)', marginRight: '5px' }}></div>
          <span>5 minutes</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
          {/* 10 minutes: matches isochrone: rgba(251, 198, 47, 0.8) */}
          <div style={{ width: '15px', height: '15px', background: 'rgba(251, 198, 47, 1)', marginRight: '5px' }}></div>
          <span>10 minutes</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
          {/* 15 minutes: matches isochrone: rgba(247, 139, 7, 0.8) */}
          <div style={{ width: '15px', height: '15px', background: 'rgba(247, 139, 7, 1)', marginRight: '5px' }}></div>
          <span>15 minutes</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
          {/* 20 minutes: matches isochrone: rgba(219, 23, 19, 0.5) */}
          <div style={{ width: '15px', height: '15px', background: 'rgba(219, 23, 19, 1)', marginRight: '5px' }}></div>
          <span>20 minutes</span>
        </div>
      </div>
 
      {/* Isochrone Percentage Legend */}
      <div
        style={{
          position: 'absolute',
          bottom: 280,
          left: 20,
          background: 'white',
          padding: '10px',
          borderRadius: '5px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          zIndex: 1000,
          width: 220,
        }}
      >
        <div style={{ marginBottom: '5px', fontWeight: 'bold' }}>Isochrone Coverage (%)</div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: '15px', height: '15px', background: 'rgba(76, 175, 80, 1)', marginRight: '5px' }}></div>
          <span>5 minutes: {isochronePercentages.percentage5}%</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
          <div style={{ width: '15px', height: '15px', background: 'rgba(251, 198, 47, 1)', marginRight: '5px' }}></div>
          <span>10 minutes: {isochronePercentages.percentage10}%</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
          <div style={{ width: '15px', height: '15px', background: 'rgba(247, 139, 7, 1)', marginRight: '5px' }}></div>
          <span>15 minutes: {isochronePercentages.percentage15}%</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
          <div style={{ width: '15px', height: '15px', background: 'rgba(219, 23, 19, 1)', marginRight: '5px' }}></div>
          <span>20 minutes: {isochronePercentages.percentage20}%</span>
        </div>
      </div>
 
      {/* Refresh Button */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
        }}
      >
        <button
          onClick={handleRefreshClick}
          style={{
            backgroundColor: '#0078D4',
            color: 'white',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold',
          }}
        >
          Reset
        </button>
      </div>
    </div>
  );
};
 
export default SanFranciscoParkingMap;